import React, { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationCircleIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { Chapter } from '../../types/book';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface ChapterFormProps {
  isOpen: boolean;
  onClose: () => void;
  onChapterCreated: () => void;
  bookId: number;
  initialChapter?: Chapter;
  isEditMode?: boolean;
}

const initialFormData: Omit<Chapter, 'id' | 'book_id'> = {
  title: '',
  chapter_number: 1,
  summary: '',
  personal_notes: '',
  is_completed: false,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

const ChapterForm: React.FC<ChapterFormProps> = ({
  isOpen,
  onClose,
  onChapterCreated,
  bookId,
  initialChapter = null,
  isEditMode = false,
}) => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [existingChapters, setExistingChapters] = useState<Chapter[]>([]);
  const initializedChapterRef = useRef<string | null>(null);

  // Fetch existing chapters to determine next chapter number
  const fetchExistingChapters = useCallback(async () => {
    try {
      const response = await apiClient.get(`/api/chapters/?book=${bookId}`);
      setExistingChapters(response.data || []);
    } catch (error) {
      console.error('Error fetching existing chapters:', error);
      setExistingChapters([]);
    }
  }, [bookId]);

  // Calculate next chapter number
  const getNextChapterNumber = useCallback(() => {
    if (existingChapters.length === 0) return 1;
    const maxChapterNumber = Math.max(...existingChapters.map(ch => ch.chapter_number));
    return maxChapterNumber + 1;
  }, [existingChapters]);

  // Fetch chapters when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchExistingChapters();
    }
  }, [isOpen, fetchExistingChapters]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      const currentChapterId = initialChapter?.id || null;
      const currentMode = isEditMode ? 'edit' : 'create';
      const trackingKey = `${currentMode}-${currentChapterId}`;

      // Only initialize if we haven't already initialized for this chapter/mode combination
      if (initializedChapterRef.current !== trackingKey) {
        if (isEditMode && initialChapter) {
          setFormData({
            title: initialChapter.title,
            chapter_number: initialChapter.chapter_number,
            summary: initialChapter.summary || '',
            personal_notes: initialChapter.personal_notes || '',
            is_completed: initialChapter.is_completed,
            created_at: initialChapter.created_at,
            updated_at: initialChapter.updated_at,
          });
        } else {
          // For new chapters, reset to initial data
          setFormData({
            ...initialFormData,
            chapter_number: 1 // Will be updated when chapters are loaded
          });
        }
        initializedChapterRef.current = trackingKey;
      }
    } else {
      // Reset when modal closes
      initializedChapterRef.current = null;
    }
  }, [isOpen, isEditMode, initialChapter]);

  // Update chapter number for new chapters after existing chapters are loaded
  useEffect(() => {
    if (isOpen && !isEditMode && existingChapters.length > 0) {
      setFormData(prev => ({
        ...prev,
        chapter_number: getNextChapterNumber()
      }));
    }
  }, [isOpen, isEditMode, existingChapters, getNextChapterNumber]);

  // Reset errors when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setSubmitError(null);
        setSubmitSuccess(false);
      }, 300);
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked :
              name === 'chapter_number' ? parseInt(value) || 1 : value
    }));
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  // Check if chapter number already exists
  const isChapterNumberTaken = (chapterNumber: number) => {
    return existingChapters.some(ch =>
      ch.chapter_number === chapterNumber &&
      (!isEditMode || ch.id !== initialChapter?.id)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    // Client-side validation
    if (isChapterNumberTaken(formData.chapter_number)) {
      setSubmitError(`Chapter number ${formData.chapter_number} already exists for this book. Please choose a different number.`);
      setIsSubmitting(false);
      return;
    }

    try {
      const chapterData = {
        ...formData,
        book: bookId,  // Changed from book_id to book to match the serializer
      };


      if (isEditMode) {
        const response = await apiClient.put(`/api/chapters/${initialChapter?.id}/`, chapterData);
        if (response.status >= 200 && response.status < 300) {
          setSubmitSuccess(true);
          onChapterCreated();
          setTimeout(() => { onClose(); }, 1500);
        } else {
          throw new Error(`Failed to update chapter (${response.status})`);
        }
      } else {
        const response = await apiClient.post('/api/chapters/', chapterData);
        if (response.status >= 200 && response.status < 300) {
          setSubmitSuccess(true);
          onChapterCreated();
          setFormData(initialFormData);
          setTimeout(() => { onClose(); }, 1500);
        } else {
          throw new Error(`Failed to create chapter (${response.status})`);
        }
      }
    } catch (err: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} chapter:`, err);

      // Extract detailed error information
      let errorMsg = `Failed to ${isEditMode ? 'update' : 'create'} chapter.`;

      if (err.response?.data) {
        const errorData = err.response.data;

        // Handle field-specific errors
        const fieldErrors = [];
        for (const [field, messages] of Object.entries(errorData)) {
          if (Array.isArray(messages)) {
            fieldErrors.push(`${field}: ${messages.join(', ')}`);
          } else if (typeof messages === 'string') {
            fieldErrors.push(`${field}: ${messages}`);
          }
        }

        if (fieldErrors.length > 0) {
          errorMsg = fieldErrors.join('\n');
        } else if (errorData.detail) {
          errorMsg = errorData.detail;
        } else if (errorData.non_field_errors) {
          errorMsg = Array.isArray(errorData.non_field_errors)
            ? errorData.non_field_errors.join('\n')
            : errorData.non_field_errors;
        }
      } else if (err.message) {
        errorMsg = err.message;
      }

      setSubmitError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Styles
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(107, 114, 128, 0.75)',
    transition: 'opacity 0.3s ease-out',
  };

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 10,
    overflowY: 'auto',
  };

  const centeredWrapperStyle: React.CSSProperties = {
    display: 'flex',
    minHeight: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACING[16],
    textAlign: 'center',
  };

  const panelStyle: React.CSSProperties = {
    position: 'relative',
    transform: 'none',
    overflow: 'hidden',
    borderRadius: RADIUS,
    backgroundColor: COLORS.bg.panel,
    paddingLeft: SPACING[16],
    paddingRight: SPACING[16],
    paddingBottom: SPACING[16],
    paddingTop: SPACING[24],
    textAlign: 'left',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    transition: 'all 0.3s ease-out',
    margin: SPACING[32],
    width: '100%',
    maxWidth: '512px',
    padding: SPACING[24],
  };

  const closeButtonContainerStyle: React.CSSProperties = {
    position: 'absolute',
    right: 0,
    top: 0,
    paddingRight: SPACING[16],
    paddingTop: SPACING[16],
    display: 'block',
  };

  const closeButtonStyle: React.CSSProperties = {
    borderRadius: RADIUS,
    backgroundColor: COLORS.bg.panel,
    color: COLORS.text.tertiary,
    border: 'none',
    cursor: 'pointer',
    padding: SPACING[4],
  };

  const iconStyle: React.CSSProperties = {
    height: '24px',
    width: '24px',
  };

  const contentWrapperStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
  };

  const formContainerStyle: React.CSSProperties = {
    marginTop: 0,
    textAlign: 'left',
    width: '100%',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: TYPOGRAPHY.size['2xl'],
    fontWeight: TYPOGRAPHY.weight.semibold,
    lineHeight: '1.5',
    color: COLORS.text.primary,
  };

  const formStyle: React.CSSProperties = {
    marginTop: SPACING[16],
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING[16],
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: TYPOGRAPHY.size.sm,
    fontWeight: TYPOGRAPHY.weight.medium,
    color: COLORS.text.secondary,
  };

  const inputStyle: React.CSSProperties = {
    marginTop: SPACING[4],
    display: 'block',
    width: '100%',
    borderRadius: RADIUS,
    border: `1px solid ${COLORS.border.default}`,
    padding: `${SPACING[8]} ${SPACING[12]}`,
    fontSize: TYPOGRAPHY.size.sm,
    fontFamily: TYPOGRAPHY.body.fontFamily,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const inputErrorStyle: React.CSSProperties = {
    ...inputStyle,
    border: `1px solid ${COLORS.status.error}`,
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    resize: 'vertical',
  };

  const checkboxContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
  };

  const checkboxStyle: React.CSSProperties = {
    height: '16px',
    width: '16px',
    borderRadius: '4px',
    border: `1px solid ${COLORS.border.default}`,
    accentColor: COLORS.accent.default,
  };

  const checkboxLabelStyle: React.CSSProperties = {
    marginLeft: SPACING[8],
    display: 'block',
    fontSize: TYPOGRAPHY.size.sm,
    color: COLORS.text.primary,
  };

  const errorBoxStyle: React.CSSProperties = {
    borderRadius: RADIUS,
    backgroundColor: '#FEF2F2',
    padding: SPACING[16],
  };

  const errorFlexStyle: React.CSSProperties = {
    display: 'flex',
  };

  const errorIconContainerStyle: React.CSSProperties = {
    flexShrink: 0,
  };

  const errorIconStyle: React.CSSProperties = {
    height: '20px',
    width: '20px',
    color: COLORS.status.error,
  };

  const errorTextContainerStyle: React.CSSProperties = {
    marginLeft: SPACING[12],
  };

  const errorTextStyle: React.CSSProperties = {
    fontSize: TYPOGRAPHY.size.sm,
    fontWeight: TYPOGRAPHY.weight.medium,
    color: '#991B1B',
    whiteSpace: 'pre-line',
  };

  const successBoxStyle: React.CSSProperties = {
    borderRadius: RADIUS,
    backgroundColor: '#F0FDF4',
    padding: SPACING[16],
  };

  const successIconStyle: React.CSSProperties = {
    height: '20px',
    width: '20px',
    color: COLORS.status.success,
  };

  const successTextStyle: React.CSSProperties = {
    fontSize: TYPOGRAPHY.size.sm,
    fontWeight: TYPOGRAPHY.weight.medium,
    color: '#166534',
  };

  const buttonContainerStyle: React.CSSProperties = {
    marginTop: SPACING[24],
    display: 'flex',
    flexDirection: 'row-reverse',
    gap: SPACING[12],
  };

  const primaryButtonStyle: React.CSSProperties = {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: RADIUS,
    backgroundColor: COLORS.accent.default,
    paddingLeft: SPACING[12],
    paddingRight: SPACING[12],
    paddingTop: SPACING[8],
    paddingBottom: SPACING[8],
    fontSize: TYPOGRAPHY.size.sm,
    fontWeight: TYPOGRAPHY.weight.semibold,
    color: '#FFFFFF',
    border: 'none',
    cursor: 'pointer',
    opacity: isSubmitting ? 0.5 : 1,
  };

  const secondaryButtonStyle: React.CSSProperties = {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: RADIUS,
    backgroundColor: COLORS.bg.panel,
    paddingLeft: SPACING[12],
    paddingRight: SPACING[12],
    paddingTop: SPACING[8],
    paddingBottom: SPACING[8],
    fontSize: TYPOGRAPHY.size.sm,
    fontWeight: TYPOGRAPHY.weight.semibold,
    color: COLORS.text.primary,
    border: `1px solid ${COLORS.border.default}`,
    cursor: 'pointer',
  };

  const spinnerStyle: React.CSSProperties = {
    height: '20px',
    width: '20px',
    marginRight: SPACING[8],
    animation: 'spin 1s linear infinite',
  };

  const validationErrorStyle: React.CSSProperties = {
    marginTop: SPACING[4],
    fontSize: TYPOGRAPHY.size.sm,
    color: COLORS.status.error,
  };

  const srOnlyStyle: React.CSSProperties = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    borderWidth: 0,
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 10 }} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={overlayStyle} />
        </Transition.Child>

        <div style={containerStyle}>
          <div style={centeredWrapperStyle}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel style={panelStyle}>
                <div style={closeButtonContainerStyle}>
                  <button
                    type="button"
                    style={closeButtonStyle}
                    onClick={onClose}
                  >
                    <span style={srOnlyStyle}>Close</span>
                    <XMarkIcon style={iconStyle} aria-hidden="true" />
                  </button>
                </div>

                <div style={contentWrapperStyle}>
                  <div style={formContainerStyle}>
                    <Dialog.Title as="h3" style={titleStyle}>
                      {isEditMode ? 'Edit Chapter' : 'Add New Chapter'}
                    </Dialog.Title>
                    <form onSubmit={handleSubmit} style={formStyle} noValidate>
                      <div>
                        <label htmlFor="title" style={labelStyle}>
                          Title
                        </label>
                        <input
                          type="text"
                          name="title"
                          id="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          style={inputStyle}
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="chapter_number" style={labelStyle}>
                          Chapter Number
                        </label>
                        <input
                          type="number"
                          name="chapter_number"
                          id="chapter_number"
                          value={formData.chapter_number}
                          onChange={handleInputChange}
                          min="1"
                          style={isChapterNumberTaken(formData.chapter_number) ? inputErrorStyle : inputStyle}
                          required
                        />
                        {isChapterNumberTaken(formData.chapter_number) && (
                          <p style={validationErrorStyle}>
                            Chapter number {formData.chapter_number} already exists
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="summary" style={labelStyle}>
                          Summary
                        </label>
                        <textarea
                          name="summary"
                          id="summary"
                          rows={4}
                          value={formData.summary}
                          onChange={handleInputChange}
                          style={textareaStyle}
                          placeholder="Enter a summary of this chapter (optional)"
                        />
                      </div>

                      <div>
                        <label htmlFor="personal_notes" style={labelStyle}>
                          Personal Notes
                        </label>
                        <textarea
                          name="personal_notes"
                          id="personal_notes"
                          rows={4}
                          value={formData.personal_notes}
                          onChange={handleInputChange}
                          style={textareaStyle}
                        />
                      </div>

                      <div style={checkboxContainerStyle}>
                        <input
                          type="checkbox"
                          name="is_completed"
                          id="is_completed"
                          checked={formData.is_completed}
                          onChange={handleInputChange}
                          style={checkboxStyle}
                        />
                        <label htmlFor="is_completed" style={checkboxLabelStyle}>
                          Mark as completed
                        </label>
                      </div>

                      {submitError && (
                        <div style={errorBoxStyle}>
                          <div style={errorFlexStyle}>
                            <div style={errorIconContainerStyle}>
                              <ExclamationCircleIcon style={errorIconStyle} aria-hidden="true" />
                            </div>
                            <div style={errorTextContainerStyle}>
                              <h3 style={errorTextStyle}>{submitError}</h3>
                            </div>
                          </div>
                        </div>
                      )}

                      {submitSuccess && (
                        <div style={successBoxStyle}>
                          <div style={errorFlexStyle}>
                            <div style={errorIconContainerStyle}>
                              <CheckCircleIcon style={successIconStyle} aria-hidden="true" />
                            </div>
                            <div style={errorTextContainerStyle}>
                              <h3 style={successTextStyle}>
                                Chapter {isEditMode ? 'updated' : 'created'} successfully!
                              </h3>
                            </div>
                          </div>
                        </div>
                      )}

                      <div style={buttonContainerStyle}>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          style={primaryButtonStyle}
                        >
                          {isSubmitting ? (
                            <>
                              <ArrowPathIcon style={spinnerStyle} />
                              {isEditMode ? 'Updating...' : 'Creating...'}
                            </>
                          ) : (
                            isEditMode ? 'Update Chapter' : 'Create Chapter'
                          )}
                        </button>
                        <button
                          type="button"
                          style={secondaryButtonStyle}
                          onClick={onClose}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ChapterForm;
